import jsPDF from 'jspdf';

// ─── THEME ────────────────────────────────────────────────────────────────────
const C = {
  bg:         [255, 255, 255],
  panel:      [242, 245, 248],
  border:     [180, 195, 210],
  amber:      [200, 95, 30],
  blue:       [0, 60, 120],
  cyan:       [0, 115, 170],
  textMain:   [15, 25, 40],
  textDim:    [90, 105, 125],
  green:      [35, 130, 70],
  red:        [195, 30, 40],
  yellow:     [180, 130, 0],
};

const PHASE_COLORS_RGB = {
  exploration:  [200, 95, 30],
  analysis:     [180, 130, 0],
  negotiation:  [120, 50, 160],
  project:      [30, 100, 180],
  opening:      [35, 130, 70],
  operational:  [0, 160, 90],
  rejected:     [195, 30, 40],
};

const PHASE_LABELS = {
  exploration: 'Exploración',
  analysis: 'Análisis',
  negotiation: 'Negociación',
  project: 'Proyecto',
  opening: 'Apertura',
  operational: 'Operativo',
  rejected: 'Rechazado',
};

const PASTA_FIELDS = [
  { key: 'parking',    label: 'Parking' },
  { key: 'access',     label: 'Acceso' },
  { key: 'signing',    label: 'Señalización' },
  { key: 'traffic',    label: 'Tráfico' },
  { key: 'activity',   label: 'Actividad' },
  { key: 'visibility', label: 'Visibilidad' },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const setFill   = (doc, rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
const setStroke = (doc, rgb) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
const setTxt    = (doc, rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
const fillRect  = (doc, x, y, w, h, color) => { setFill(doc, color); doc.rect(x, y, w, h, 'F'); };

const txt = (doc, text, x, y, size, color, align = 'left') => {
  doc.setFontSize(size);
  setTxt(doc, color);
  doc.text(String(text), x, y, { align });
};

const hLine = (doc, x, y, w, color, lw = 0.3) => {
  setStroke(doc, color);
  doc.setLineWidth(lw);
  doc.line(x, y, x + w, y);
};

const sectionTitle = (doc, text, x, y, color) => {
  fillRect(doc, x, y - 4, 186, 7, [...color, 15].slice(0, 3));
  setStroke(doc, color);
  doc.setLineWidth(0.4);
  doc.rect(x, y - 4, 186, 7, 'S');
  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  setTxt(doc, color);
  doc.text(text.toUpperCase(), x + 3, y, {});
  doc.setFont(undefined, 'normal');
  return y + 7;
};

const infoRow = (doc, label, value, x, y) => {
  doc.setFontSize(7.5);
  doc.setFont(undefined, 'bold');
  setTxt(doc, C.textDim);
  doc.text(label, x, y, {});
  doc.setFont(undefined, 'normal');
  setTxt(doc, C.textMain);
  const lines = doc.splitTextToSize(String(value || '—'), 130);
  doc.text(lines, x + 55, y, {});
  return y + Math.max(5, lines.length * 4.5);
};

const addPage = (doc) => {
  doc.addPage();
  fillRect(doc, 0, 0, 210, 297, C.bg);
  return 20;
};

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
export const exportOpportunityToPdf = async (opportunity) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;
  const MARGIN = 12;
  let y = 0;

  const phase = opportunity.phase || 'exploration';
  const phaseColor = PHASE_COLORS_RGB[phase] || C.amber;
  const phaseName = PHASE_LABELS[phase] || phase;

  // ── PAGE 1 ────────────────────────────────────────────────────────────────

  fillRect(doc, 0, 0, W, 297, C.bg);

  // Header stripe
  fillRect(doc, 0, 0, W, 22, C.blue);
  hLine(doc, 0, 22, W, phaseColor, 1.2);

  // Title in header
  doc.setFont(undefined, 'bold');
  txt(doc, 'FICHA DE OPORTUNIDAD', MARGIN, 9, 9, [255, 255, 255]);
  doc.setFont(undefined, 'normal');
  txt(doc, `OPPORTUNITY SYSTEM · ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}`, MARGIN, 16, 6.5, [150, 180, 210]);

  // Phase badge (top right)
  fillRect(doc, 155, 4, 44, 14, phaseColor);
  doc.setFont(undefined, 'bold');
  txt(doc, phaseName.toUpperCase(), 177, 12.5, 8, [255, 255, 255], 'center');
  doc.setFont(undefined, 'normal');

  y = 28;

  // ── OPPORTUNITY NAME ──────────────────────────────────────────────────────
  doc.setFont(undefined, 'bold');
  txt(doc, opportunity.name || '—', MARGIN, y + 7, 14, C.textMain);
  doc.setFont(undefined, 'normal');
  y += 12;

  if (opportunity.address) {
    txt(doc, `📍 ${opportunity.address}`, MARGIN, y, 8, C.textDim);
    y += 6;
  }

  const coords = opportunity.location?.coordinates;
  if (coords) {
    txt(doc, `${coords[1].toFixed(6)}°N  ${coords[0].toFixed(6)}°E`, MARGIN, y, 7, C.textDim);
    y += 5;
  }

  hLine(doc, MARGIN, y + 2, W - MARGIN * 2, C.border);
  y += 7;

  // ── GENERAL INFO ──────────────────────────────────────────────────────────
  y = sectionTitle(doc, 'Información General', MARGIN, y, phaseColor);
  y += 1;

  y = infoRow(doc, 'ID Sistema', opportunity._id, MARGIN, y);
  y = infoRow(doc, 'Fuente', opportunity.source || '—', MARGIN, y);
  y = infoRow(doc, 'País / Región', opportunity.country || '—', MARGIN, y);
  y = infoRow(doc, 'Creado el', new Date(opportunity.ingestedAt || opportunity.updatedAt).toLocaleDateString('es-ES'), MARGIN, y);
  y += 4;

  // ── PASTA V SCORES ────────────────────────────────────────────────────────
  if (opportunity.pastaScores && Object.values(opportunity.pastaScores).some(v => v)) {
    y = sectionTitle(doc, 'PASTA V Scores', MARGIN, y, phaseColor);
    y += 2;

    const colW = (W - MARGIN * 2 - 5) / 3;
    let col = 0;
    let rowY = y;

    PASTA_FIELDS.forEach((f, i) => {
      const score = opportunity.pastaScores[f.key];
      if (!score) return;
      const cx = MARGIN + col * (colW + 2.5);

      fillRect(doc, cx, rowY, colW, 14, C.panel);
      setStroke(doc, C.border);
      doc.setLineWidth(0.2);
      doc.rect(cx, rowY, colW, 14, 'S');

      doc.setFontSize(6.5);
      doc.setFont(undefined, 'bold');
      setTxt(doc, C.textDim);
      doc.text(f.label.toUpperCase(), cx + 3, rowY + 4, {});
      doc.setFont(undefined, 'normal');

      const barW = colW - 6;
      const fillPct = (score / 10);
      const barColor = score >= 7 ? C.green : score >= 4 ? C.yellow : C.red;

      fillRect(doc, cx + 3, rowY + 5.5, barW, 3, [220, 225, 230]);
      fillRect(doc, cx + 3, rowY + 5.5, barW * fillPct, 3, barColor);

      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      setTxt(doc, C.textMain);
      doc.text(String(score), cx + colW - 5, rowY + 12, { align: 'right' });
      doc.setFont(undefined, 'normal');

      col++;
      if (col >= 3) { col = 0; rowY += 17; }
    });

    y = rowY + (col > 0 ? 17 : 0) + 2;
  }

  // ── PHASE HISTORY ─────────────────────────────────────────────────────────
  if (opportunity.phaseHistory && opportunity.phaseHistory.length > 0) {
    y = sectionTitle(doc, 'Historial de Fases', MARGIN, y, phaseColor);
    y += 2;

    [...opportunity.phaseHistory].reverse().forEach(h => {
      const pc = PHASE_COLORS_RGB[h.phase] || C.textDim;
      fillRect(doc, MARGIN, y - 3, 4, 4, pc);
      doc.setFontSize(7.5);
      doc.setFont(undefined, 'bold');
      setTxt(doc, pc);
      doc.text((PHASE_LABELS[h.phase] || h.phase).toUpperCase(), MARGIN + 6, y, {});
      doc.setFont(undefined, 'normal');
      setTxt(doc, C.textDim);
      doc.text(new Date(h.changedAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }), 130, y, {});
      y += 4;
      if (h.notes) {
        setTxt(doc, C.textDim);
        doc.setFontSize(7);
        const lines = doc.splitTextToSize(h.notes, W - MARGIN * 2 - 10);
        doc.text(lines, MARGIN + 6, y, {});
        y += lines.length * 4 + 2;
      }
    });
    y += 3;
  }

  // ── CONTACTS ──────────────────────────────────────────────────────────────
  if (opportunity.contacts && opportunity.contacts.length > 0) {
    if (y > 240) { y = addPage(doc); }
    y = sectionTitle(doc, 'Contactos', MARGIN, y, phaseColor);
    y += 2;

    opportunity.contacts.forEach(c => {
      doc.setFontSize(8.5);
      doc.setFont(undefined, 'bold');
      setTxt(doc, C.textMain);
      doc.text(c.name, MARGIN, y, {});
      doc.setFont(undefined, 'normal');
      if (c.role) {
        setTxt(doc, C.textDim);
        doc.setFontSize(7);
        doc.text(`(${c.role})`, MARGIN + doc.getTextWidth(c.name) + 3, y, {});
      }
      y += 5;
      if (c.company) { setTxt(doc, C.textDim); doc.setFontSize(7); doc.text(`Empresa: ${c.company}`, MARGIN + 4, y, {}); y += 4; }
      if (c.phone)   { setTxt(doc, C.textDim); doc.setFontSize(7); doc.text(`Tel: ${c.phone}`, MARGIN + 4, y, {}); y += 4; }
      if (c.email)   { setTxt(doc, C.textDim); doc.setFontSize(7); doc.text(`Email: ${c.email}`, MARGIN + 4, y, {}); y += 4; }
      if (c.notes)   { setTxt(doc, [130, 130, 130]); doc.setFontSize(6.5); const lines = doc.splitTextToSize(c.notes, W - MARGIN * 2 - 8); doc.text(lines, MARGIN + 4, y, {}); y += lines.length * 4; }
      hLine(doc, MARGIN, y, W - MARGIN * 2, C.border);
      y += 5;
    });
  }

  // ── JOURNAL ───────────────────────────────────────────────────────────────
  if (opportunity.journal && opportunity.journal.length > 0) {
    if (y > 230) { y = addPage(doc); }
    y = sectionTitle(doc, 'Diario', MARGIN, y, phaseColor);
    y += 2;

    [...opportunity.journal].reverse().forEach(e => {
      const ec = PHASE_COLORS_RGB[e.phase] || C.textDim;
      fillRect(doc, MARGIN, y - 2, 2, 2, ec);
      setTxt(doc, C.textDim);
      doc.setFontSize(6.5);
      const dateStr = new Date(e.createdAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
      doc.text(dateStr, MARGIN + 4, y, {});
      if (e.phase) { setTxt(doc, ec); doc.text(`[${PHASE_LABELS[e.phase] || e.phase}]`, MARGIN + 30, y, {}); }
      y += 4;
      setTxt(doc, C.textMain);
      doc.setFontSize(7.5);
      const lines = doc.splitTextToSize(e.text, W - MARGIN * 2 - 6);
      doc.text(lines, MARGIN + 4, y, {});
      y += lines.length * 4 + 3;

      if (y > 270) { y = addPage(doc); }
    });
  }

  // ── TASKS ─────────────────────────────────────────────────────────────────
  if (opportunity.tasks && opportunity.tasks.length > 0) {
    if (y > 240) { y = addPage(doc); }
    y = sectionTitle(doc, 'Tareas', MARGIN, y, phaseColor);
    y += 2;

    opportunity.tasks.forEach(t => {
      const tc = t.done ? C.green : C.textDim;
      doc.setFontSize(7.5);
      setTxt(doc, tc);
      doc.text(t.done ? '☑' : '☐', MARGIN, y, {});
      setTxt(doc, t.done ? C.textDim : C.textMain);
      const lines = doc.splitTextToSize(t.title, W - MARGIN * 2 - 10);
      doc.text(lines, MARGIN + 6, y, {});
      y += lines.length * 4 + 2;
    });
  }

  // ── FOOTER ────────────────────────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    hLine(doc, 0, 288, W, C.border);
    doc.setFontSize(6);
    setTxt(doc, C.textDim);
    doc.text(`OPPORTUNITY SYSTEM · ID: ${opportunity._id}`, MARGIN, 293, {});
    doc.text(`Página ${i} / ${pageCount}`, W - MARGIN, 293, { align: 'right' });
  }

  const safeName = (opportunity.name || 'oportunidad').replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
  doc.save(`oportunidad_${safeName}.pdf`);
};
